/**
 * ATL-000537 — Escape-key layer stack.
 *
 * ## The bug this exists to fix
 *
 * Every dismissible surface used to attach its OWN `document` keydown listener
 * and close itself on Escape. With a calendar open inside the USPS Add Trip
 * drawer, one Escape fired *both* listeners: the calendar closed AND the drawer
 * closed, discarding everything the operator had typed.
 *
 * `stopPropagation()` cannot fix that. Both listeners are registered on the
 * SAME node (`document`), and propagation control only governs movement
 * BETWEEN nodes — sibling listeners on one node still all run (that needs
 * `stopImmediatePropagation`, whose effect then depends on registration order,
 * which is mount order: the drawer registers first, so it would win anyway).
 *
 * ## How this fixes it
 *
 * One `document` listener for the whole app, plus an ordered stack of layers.
 * On Escape, only the TOP-most active layer's handler runs. Layers are ordered
 * by when they became active, so a picker opened inside a drawer sits above
 * that drawer and consumes the key by itself.
 *
 * The listener stays in the BUBBLE phase and deliberately does NOT call
 * `preventDefault()` / `stopPropagation()`. Non-layer consumers must keep
 * seeing Escape — most importantly AG Grid, which cancels an in-progress cell
 * edit on Escape, and the Add Trip drawer is full of editable sub-grids.
 * Swallowing the key would trade one bug for another.
 *
 * ## Who should be a layer
 *
 * Anything that closes itself on Escape via a `document` listener: `Modal`,
 * `Drawer`, `DatePicker`, `TimePicker`, `DateTimePicker`, `MultiSelect`,
 * `MultiSelectDropdown`, `Menu`.
 *
 * Components that handle Escape with a React `onKeyDown` on their own element
 * (`Select`, `SearchableSelect`) don't need to register — their handler runs
 * before the event reaches `document`, so they just need to call
 * `stopPropagation()` so this listener doesn't also fire.
 *
 * ## Ordering rule (and its one sharp edge)
 *
 * "Top-most" means MOST RECENTLY ACTIVATED, not deepest in the tree. That is
 * exactly right for the real interaction: a drawer opens, then the operator
 * clicks a field and its popover activates afterwards, so the popover wins.
 *
 * The edge: React runs child effects BEFORE parent effects, so a layer that is
 * already active on its first mount INSIDE another layer registers *before* its
 * own ancestor and the ancestor would win. No shipped surface does that — every
 * popover here starts closed and activates on interaction — but a future
 * component that mounts already-open inside a Modal/Drawer must not rely on
 * nesting to win. Toggle it open after mount (the normal pattern) and ordering
 * is correct.
 */
import { useEffect, useRef } from 'react';

interface EscapeLayer {
  /** Monotonic activation order — highest is top-most. */
  seq: number;
  /** Read through a ref so re-renders never re-register the layer. */
  handlerRef: { current: () => void };
}

const layers: EscapeLayer[] = [];
let seqCounter = 0;
let listening = false;

function onKeyDown(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return;
  if (layers.length === 0) return;
  // Top-most = highest activation sequence. Only it reacts.
  let top = layers[0]!;
  for (const l of layers) {
    if (l.seq > top.seq) top = l;
  }
  top.handlerRef.current();
}

function addLayer(layer: EscapeLayer): void {
  layers.push(layer);
  if (!listening) {
    document.addEventListener('keydown', onKeyDown);
    listening = true;
  }
}

function removeLayer(layer: EscapeLayer): void {
  const i = layers.indexOf(layer);
  if (i !== -1) layers.splice(i, 1);
  if (layers.length === 0 && listening) {
    document.removeEventListener('keydown', onKeyDown);
    listening = false;
  }
}

/**
 * Register as the top-most Escape target while `active` is true.
 *
 * `onEscape` is read through a ref, so an inline arrow function is fine — the
 * layer's position in the stack is decided by when `active` flipped to true and
 * never churns on re-render.
 *
 * Pass a no-op `onEscape` to CONSUME Escape without acting on it: the layer
 * still occupies the top slot, so an ancestor layer won't close either. That is
 * how `Drawer`'s `closeOnEscape={false}` makes Escape do nothing at all.
 */
export function useEscapeLayer(active: boolean, onEscape: () => void): void {
  const handlerRef = useRef(onEscape);
  handlerRef.current = onEscape;

  useEffect(() => {
    if (!active) return;
    const layer: EscapeLayer = { seq: ++seqCounter, handlerRef };
    addLayer(layer);
    return () => removeLayer(layer);
  }, [active]);
}

/** Test-only: how many layers are currently registered. */
export function __escapeLayerCount(): number {
  return layers.length;
}
